import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

// local imports
import DateUtil from 'utils/DateUtil'
import DatePickerIcon from 'components/ui/DatePickerIcon'
import { IFilteredPoolVehicleList } from 'types/PoolVehicleType'

interface ModalHeaderProps {
  availableDateRange: { start: number; end: number }
  filterDate: Date
  setFilterDate: Dispatch<SetStateAction<Date>>
  vehicle: IFilteredPoolVehicleList
  setTableWeek: Dispatch<SetStateAction<{ start: Date; end: Date }>>
  selectedDateStr?: string
}

export default function ReservationModalHeader({
  availableDateRange: availableDates,
  selectedDateStr,
  filterDate,
  setFilterDate,
  setTableWeek,
  vehicle,
}: ModalHeaderProps) {
  const { t } = useTranslation()
  const {
    plate,
    brandOfVehicle,
    modelOfVehicle,
    locationToReceive,
    locationToDeliver,
    locationToVehicle: { districtName, firmName },
  } = vehicle

  // Variables
  const vehicleInfo = `${brandOfVehicle} - ${modelOfVehicle}`
  const vehicleLocationName = `${firmName},${districtName}`

  /* Handle Reservation Date change input */
  const handleDateChange = (e: any) => {
    if (e.value) {
      const { start, end } = DateUtil.getWeekStartEndFromDate(e.value)
      setTableWeek({ start, end })
      setFilterDate(e.value)
    }
  }

  return (
    <PoolVehicleHeaderInfo>
      <div className="vehicle-info">
        <div className="vehicle-summary">
          <span className="vehicle-name">
            {vehicleInfo} ({plate})
          </span>
          <span className="vehicle-info-text"> {vehicleLocationName || '---'} </span>
          <span className="vehicle-info-text">
            {locationToReceive} - {locationToDeliver}{' '}
          </span>
        </div>
      </div>

      <div className="d-flex flex-column align-items-center justify-content-end">
        {!!selectedDateStr ? (
          <div className="calendar-notify">{t('calendar.selected-day-info-text', { date: selectedDateStr })}</div>
        ) : null}
      </div>

      <div className="appointment-date">
        <DatePickerIcon
          idName="poolVehicleModalReservationDate"
          showOtherMonths
          selectOtherMonths
          showOnFocus
          appendTo={document.body}
          disabledDays={[]}
          defaultDate={filterDate}
          minDate={DateUtil.getPastDate(availableDates.start)}
          maxDate={DateUtil.getFutureDate(availableDates.end)}
          label={t('input-texts.appointment-date')}
          onSelectedDateChange={handleDateChange}
          onFocus={() => {}}
        />
      </div>
    </PoolVehicleHeaderInfo>
  )
}

const PoolVehicleHeaderInfo = styled.div`
  margin: 0.5rem 0 0.5rem 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .vehicle-info {
    position: relative;
    width: 360px;
    display: flex;
    align-items: flex-start;

    .vehicle-summary {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 5px;
      margin-right: 40px;

      .vehicle-info-text {
        font-size: 12px;
        font-weight: 500;
        text-overflow: ellipsis;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
      }

      .vehicle-name {
        font-size: 14px;
        font-weight: 700;
      }
    }
  }

  .capacity-container {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 0.5rem;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    column-gap: 4px;
  }

  .calendar-notify {
    max-width: 410px;
    margin: 1rem 0;
    font-size: 14px;
    color: #222e47;
    line-height: 17px;
    font-weight: 700;
  }

  .appointment-date {
    min-width: 265px;
  }
`
